"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiXTdhTokensPage } from "@/generated/models/ApiXTdhTokensPage";
import { commonApiFetch } from "@/services/api/common-api";
export type XtdhTokensSortField = "xtdh" | "xtdh_rate";
export type XtdhTokensOrder = "ASC" | "DESC";
interface UseXtdhTokensQueryParams {
  readonly identity?: string | null | undefined;
  readonly contract?: string | null | undefined;
  readonly pageSize?: number | undefined;
  readonly sortField?: XtdhTokensSortField | undefined;
  readonly order?: XtdhTokensOrder | undefined;
  readonly enabled?: boolean | undefined;
  readonly requireIdentity?: boolean | undefined;
}
type XtdhTokensInfiniteData = InfiniteData<ApiXTdhTokensPage>;
type UseXtdhTokensQueryResult = UseInfiniteQueryResult<XtdhTokensInfiniteData, Error> & {
  readonly tokens: ApiXTdhTokensPage["data"];
  readonly errorMessage?: string | undefined;
  readonly isEnabled: boolean;
};
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: XtdhTokensSortField = "xtdh";
const DEFAULT_ORDER: XtdhTokensOrder = "DESC";
const DEFAULT_STALE_TIME = 30_000;
export function useXtdhTokensQuery(t0) {
  const $ = _c(35);
  const {
    identity,
    contract,
    pageSize: t1,
    sortField: t2,
    order: t3,
    enabled: t4,
    requireIdentity: t5
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const sortField = t2 === undefined ? DEFAULT_SORT_FIELD : t2;
  const order = t3 === undefined ? DEFAULT_ORDER : t3;
  const enabled = t4 === undefined ? true : t4;
  const requireIdentity = t5 === undefined ? true : t5;
  let t6;
  if ($[0] !== identity) {
    t6 = identity?.trim() ?? "";
    $[0] = identity;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const normalizedIdentity = t6;
  let t7;
  if ($[2] !== contract) {
    t7 = contract?.trim() ?? "";
    $[2] = contract;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const normalizedContract = t7;
  const hasIdentity = Boolean(normalizedIdentity);
  const identityRequirementMet = requireIdentity ? hasIdentity : true;
  const hasRequiredFilters = identityRequirementMet && Boolean(normalizedContract);
  const isEnabled = hasRequiredFilters && enabled;
  let t8;
  if ($[4] !== pageSize) {
    t8 = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : DEFAULT_PAGE_SIZE;
    $[4] = pageSize;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const normalizedPageSize = t8;
  const normalizedOrder = order === "ASC" ? "ASC" : "DESC";
  let t9;
  if ($[6] !== normalizedIdentity) {
    t9 = normalizedIdentity.toLowerCase();
    $[6] = normalizedIdentity;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== normalizedContract) {
    t10 = normalizedContract.toLowerCase();
    $[8] = normalizedContract;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== normalizedOrder || $[11] !== normalizedPageSize || $[12] !== requireIdentity || $[13] !== sortField || $[14] !== t10 || $[15] !== t9) {
    t11 = [QueryKey.XTDH_TOKENS, t9, t10, normalizedPageSize, sortField, normalizedOrder, requireIdentity];
    $[10] = normalizedOrder;
    $[11] = normalizedPageSize;
    $[12] = requireIdentity;
    $[13] = sortField;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const queryKey = t11;
  let t12;
  if ($[17] !== hasIdentity || $[18] !== normalizedContract || $[19] !== normalizedIdentity || $[20] !== normalizedOrder || $[21] !== normalizedPageSize || $[22] !== sortField) {
    t12 = async t13 => {
      const {
        pageParam
      } = t13;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      const params = {
        page: currentPage.toString(),
        page_size: normalizedPageSize.toString(),
        sort: sortField,
        order: normalizedOrder,
        contract: normalizedContract
      };
      if (hasIdentity) {
        params.identity = normalizedIdentity;
      }
      return commonApiFetch({
        endpoint: "xtdh/tokens",
        params
      });
    };
    $[17] = hasIdentity;
    $[18] = normalizedContract;
    $[19] = normalizedIdentity;
    $[20] = normalizedOrder;
    $[21] = normalizedPageSize;
    $[22] = sortField;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isEnabled || $[25] !== queryKey || $[26] !== t12) {
    t13 = {
      queryKey,
      queryFn: t12,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled: isEnabled,
      staleTime: DEFAULT_STALE_TIME,
      placeholderData: keepPreviousData
    };
    $[24] = isEnabled;
    $[25] = queryKey;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const query = useInfiniteQuery(t13);
  let t14;
  if ($[28] !== query.data?.pages) {
    t14 = query.data?.pages.flatMap(_temp2) ?? [];
    $[28] = query.data?.pages;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const tokens = t14;
  const errorMessage = query.error instanceof Error ? query.error.message : undefined;
  let t15;
  if ($[30] !== errorMessage || $[31] !== isEnabled || $[32] !== query || $[33] !== tokens) {
    t15 = {
      ...query,
      tokens,
      errorMessage,
      isEnabled
    };
    $[30] = errorMessage;
    $[31] = isEnabled;
    $[32] = query;
    $[33] = tokens;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}
function _temp2(pageData) {
  return pageData.data;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}